////////////////////////////////////////////
// EVM WALLET PROVIDER — wagmi (ROBINHOOD CHAIN)
////////////////////////////////////////////
//
// REPLACES THE VANILLA APP'S wallet-evm.js + VENDORED ethers/walletconnect:
//   - EIP-6963 MULTI-WALLET DISCOVERY IS WAGMI'S DEFAULT (EVERY INSTALLED
//     WALLET APPEARS AS ITS OWN CONNECTOR)
//   - WALLETCONNECT (REOWN) IS THE QR/MOBILE FALLBACK CONNECTOR
//   - CHAIN ADD/SWITCH PROMPTS COME FROM wagmi switchChain (4902 HANDLED BY
//     PASSING THE FULL CHAIN DEFINITION)
// BOTH RH NETS ARE REGISTERED; THE ACTIVE ONE IS PICKED PER-CALL VIA chainId.
import { type ReactNode } from "react";
import { http, WagmiProvider, createConfig } from "wagmi";
import { metaMask, walletConnect } from "wagmi/connectors";
import { defineChain } from "viem";
import { isMobileUA } from "@/lib/wallet/useSolWallet";
import {
  RH_CHAIN_ID_MAINNET, RH_CHAIN_ID_TESTNET,
  RH_EXPLORER_MAINNET, RH_EXPLORER_TESTNET,
  RH_PUBLIC_RPC_MAINNET, RH_PUBLIC_RPC_TESTNET,
  WC_PROJECT_ID,
} from "@/config/protocol";

// CHAIN DEFINITIONS — PUBLIC KEYLESS RPC ONLY (SAFE TO REACH WALLET CONFIG)
const robinhoodChain = defineChain({
  id: RH_CHAIN_ID_MAINNET,
  name: "Robinhood Chain",
  nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
  rpcUrls: { default: { http: [RH_PUBLIC_RPC_MAINNET] } },
  blockExplorers: { default: { name: "Blockscout", url: RH_EXPLORER_MAINNET } },
});

const robinhoodTestnet = defineChain({
  id: RH_CHAIN_ID_TESTNET,
  name: "Robinhood Chain Testnet",
  nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
  rpcUrls: { default: { http: [RH_PUBLIC_RPC_TESTNET] } },
  blockExplorers: { default: { name: "Blockscout", url: RH_EXPLORER_TESTNET } },
  testnet: true,
});

const wagmiConfig = createConfig({
  chains: [robinhoodChain, robinhoodTestnet],
  connectors: [
    // METAMASK — THE OFFICIAL SDK CONNECTOR, NEVER WALLETCONNECT: METAMASK
    // MOBILE'S WC DEEPLINK HANDLING HAS YEARS-OLD OPEN BUGS (APP OPENS, NO
    // PROMPT — metamask-mobile #5212/#4955/#6457), WHICH IS WHY RAINBOWKIT
    // MOVED METAMASK ONTO THE SDK TOO. ON DESKTOP IT DRIVES THE EXTENSION;
    // ON MOBILE IT DEEPLINKS INTO THE APP OVER ITS OWN TRANSPORT.
    metaMask({
      dappMetadata: {
        name: "Fuze",
        // THE REAL ORIGIN — METAMASK SHOWS IT ON THE CONNECT SCREEN AND ITS
        // RETURN-TO-DAPP HINTS KEY OFF THE CONNECTING SITE, SO A HARDCODED
        // DOMAIN WOULD MISLABEL DEV/STAGING SESSIONS
        url: typeof location !== "undefined" ? location.origin : "https://litfuze.fun",
        iconUrl: "https://litfuze.fun/logo.png",
      },
    }),
    walletConnect({
      projectId: WC_PROJECT_ID,
      // DESKTOP: THE REOWN QR SHEET. MOBILE: HEADLESS — THE CONNECT MODAL'S
      // CURATED WALLET ROWS DEEPLINK STRAIGHT TO THE CHOSEN APP INSTEAD
      // (lib/wallet/wc-deeplink), SO NO SECOND SHEET EVER POPS OVER THEM.
      showQrModal: !isMobileUA(),
      metadata: {
        name: "Fuze",
        description: "Fair-launch launchpad",
        url: "https://litfuze.fun",
        icons: ["https://litfuze.fun/logo.png"],
      },
    }),
  ],
  transports: {
    [robinhoodChain.id]: http(RH_PUBLIC_RPC_MAINNET),
    [robinhoodTestnet.id]: http(RH_PUBLIC_RPC_TESTNET),
  },
});

export function EvmProvider({ children }: { children: ReactNode }) {

  return <WagmiProvider config={wagmiConfig}>{children}</WagmiProvider>;
}
